import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ManageAdminApiSecretsRootParamList } from "../../../../types";
import {
  Button,
  Card,
  Page,
  Stack,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import {
  GenerateAdminApiSecretForm,
  GenerateAdminApiSecretFormProps,
  GenerateAdminApiSecretFormValues,
} from "../../components/GenerateAdminApiSecretForm";
import { Controller, useFormContext } from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import { useTranslation } from "react-i18next";
import { MUTATION_GENERATE_ACCESS_TOKEN } from "../../common";
import { AccessTokenSession } from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";

export type CreateAdminApiSecretScreenProps = NativeStackScreenProps<
  ManageAdminApiSecretsRootParamList,
  "CreateAdminApiSecretScreen"
>;

const Content = ({ navigation }: CreateAdminApiSecretScreenProps) => {
  const { t } = useTranslation("settings");

  const { control, setValue, formState, handleSubmit, watch } =
    useFormContext<GenerateAdminApiSecretFormValues>();

  const [generateApiSecretFn] = useMutation<{
    generateAccessToken: AccessTokenSession;
  }>(MUTATION_GENERATE_ACCESS_TOKEN);

  const flashMessagesContext = useFlashMessages();

  const handleOnSubmit = (data: GenerateAdminApiSecretFormValues) => {
    generateApiSecretFn({
      variables: {
        input: {
          name: data.name,
        },
      },
    })
      .then((result) => {
        if (result.data?.generateAccessToken) {
          flashMessagesContext.showMessage({
            severity: "success",
            message: t("message.api_secret_generated"),
          });
          navigation.goBack();
        } else {
          throw new Error();
        }
      })
      .catch((error) => {
        console.error(error);
        flashMessagesContext.showMessage({
          severity: "error",
          message: t("message.api_secret_generation_failed"),
        });
      });
  };

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.generate_api_secret")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title="Generate new API secret"
      secondaryTitle="Generate a new API secret for your account. Please ensure you store it securely."
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="Details">
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value } }) => (
              <TextField label="Name" value={value} onChangeText={onChange} />
            )}
          />
        </Card>
      </Stack>
    </Page>
  );
};

export const CreateAdminApiSecretScreen = (
  props: CreateAdminApiSecretScreenProps
) => {
  return (
    <GenerateAdminApiSecretForm>
      <Content {...props} />
    </GenerateAdminApiSecretForm>
  );
};
